import { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Link2, Link2Off, List, ListOrdered } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Tooltip } from '@/shared/ui/tooltip';

interface HtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

function normalizeOutgoingHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || trimmed === '<p></p>' || trimmed === '<p><br></p>') return '';
  return trimmed;
}

/** Compara conteúdo sem attrs extras do TipTap (`target`/`rel` em `<a>`, `<br>` vs `<br/>`). */
function editorHtmlEquivalent(a: string, b: string): boolean {
  const key = (html: string) =>
    html
      .replace(/<br\s*\/?>/gi, '<br>')
      .replace(/\s+target="[^"]*"/gi, '')
      .replace(/\s+rel="[^"]*"/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  return key(normalizeOutgoingHtml(a)) === key(normalizeOutgoingHtml(b));
}

function isInList(view: EditorView): boolean {
  const $from = view.state.selection.$from;
  for (let depth = $from.depth; depth > 0; depth--) {
    const name = $from.node(depth).type.name;
    if (name === 'bulletList' || name === 'orderedList') return true;
  }
  return false;
}

function ToolbarButton({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip content={label} side="top" delayDuration={200}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        aria-pressed={pressed}
        className={cn(pressed && 'bg-foreground/10 text-foreground')}
        onClick={onClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
}

/** Editor HTML leve (TipTap) — negrito, itálico, listas e link, no estilo shadcn. */
export function HtmlEditor({ value, onChange, placeholder, className, id }: HtmlEditorProps) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Digite o conteúdo…',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        id: id ?? '',
        class: cn(
          'min-h-[7.5rem] px-3 py-2 text-sm outline-none',
          '[&_a]:text-primary [&_a]:underline',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_p]:my-0',
        ),
      },
      handleKeyDown: (view, event) => {
        if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
          return false;
        }
        if (isInList(view)) return false;
        const hardBreak = view.state.schema.nodes.hardBreak;
        if (!hardBreak) return false;
        view.dispatch(view.state.tr.replaceSelectionWith(hardBreak.create()).scrollIntoView());
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(normalizeOutgoingHtml(ed.getHTML()));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (!editorHtmlEquivalent(current, next)) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const href = /^https?:\/\//i.test(url) || url.startsWith('mailto:') ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setLinkOpen(false);
    setLinkUrl('');
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 bg-muted/40 px-1 py-1">
        <ToolbarButton
          label="Negrito (⌘B)"
          pressed={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Itálico (⌘I)"
          pressed={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Lista"
          pressed={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Lista numerada"
          pressed={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-3.5" />
        </ToolbarButton>

        <Popover
          open={linkOpen}
          onOpenChange={(open) => {
            setLinkOpen(open);
            if (open) {
              setLinkUrl(editor.getAttributes('link').href ?? '');
            }
          }}
        >
          <Tooltip content="Link" side="top" delayDuration={200}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Link"
                aria-pressed={editor.isActive('link')}
                className={cn(editor.isActive('link') && 'bg-foreground/10 text-foreground')}
              >
                <Link2 className="size-3.5" />
              </Button>
            </PopoverTrigger>
          </Tooltip>
          <PopoverContent align="start" className="w-72 p-3">
            <div className="space-y-2">
              <Label htmlFor={`${id ?? 'html'}-link`}>URL do link</Label>
              <Input
                id={`${id ?? 'html'}-link`}
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://… ou mailto:…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                }}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setLinkOpen(false)}>
                  Cancelar
                </Button>
                <Button type="button" size="sm" onClick={applyLink}>
                  Aplicar
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {editor.isActive('link') ? (
          <ToolbarButton
            label="Remover link"
            onClick={() => editor.chain().focus().unsetLink().run()}
          >
            <Link2Off className="size-3.5" />
          </ToolbarButton>
        ) : null}
      </div>

      <EditorContent editor={editor} className="tiptap" />
    </div>
  );
}
